import { useState } from 'react';
import {
  Check,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  MailCheck,
  Pencil,
  RefreshCw,
  UserPlus,
  X,
} from 'lucide-react';
import type { Person, UserRole } from '../../../lib/peopleApi';
import {
  useCreatePersonAccount,
  useUpdatePersonAccount,
  useVerifyPersonEmail,
} from '../../../hooks/usePeople';
import { usePermissions } from '../../../hooks/usePermissions';
import { useRoleTemplates } from '../../../hooks/useRoles';
import { Select } from '../../ui/Select';
import { generatePassword, isStrongPassword } from '../../../lib/passwordGenerator';

const ROLE_LABELS: Record<UserRole, string> = {
  ADMIN: 'Administrator',
  MANAGER: 'Manager',
  MEMBER: 'Membru',
  VIEWER: 'Vizualizator',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function formatDateTime(value: string | null): string {
  if (!value) return 'Niciodată';
  return new Date(value).toLocaleString('ro-RO');
}

/**
 * "Cont utilizator" card on the Team Member detail page. Shows the linked
 * login account (or its absence) and — for people.canManageRoles — lets the
 * admin edit the account details or provision an account after the fact
 * (deferred account creation, POST /api/people/:id/account).
 */
export function PersonAccountSection({ person }: { person: Person }) {
  const { can } = usePermissions();
  const canManage = can('people', 'canManageRoles');
  const account = person.account ?? null;

  const [showEdit, setShowEdit] = useState(false);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-semibold text-gray-800">
          <KeyRound size={15} className="text-gray-400" />
          Cont utilizator
        </h2>
        {account && canManage && (
          <button
            type="button"
            onClick={() => setShowEdit(true)}
            className="inline-flex items-center gap-1 rounded-full border border-dashed border-gray-300 px-2.5 py-0.5 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-700"
          >
            <Pencil size={11} />
            Editează
          </button>
        )}
      </div>

      {account ? (
        <dl className="mt-2 space-y-1 text-sm text-gray-600">
          <div className="flex justify-between">
            <dt>Email autentificare</dt>
            <dd className="text-gray-900">{account.email}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Nume afișat</dt>
            <dd className="text-gray-900">{account.name}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Rol</dt>
            <dd className="text-gray-900">{ROLE_LABELS[account.role] ?? account.role}</dd>
          </div>
          <div className="flex justify-between">
            <dt>Status</dt>
            <dd>
              <span
                className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
                  account.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}
              >
                {account.isActive ? 'Activ' : 'Dezactivat'}
              </span>
            </dd>
          </div>
          {account.deletionRequestedAt && (
            <p className="rounded-md bg-red-500/10 px-2 py-1.5 text-xs text-red-700">
              A cerut ștergerea contului pe {formatDateTime(account.deletionRequestedAt)}. Ca să
              finalizezi, folosește „Șterge membru”.
            </p>
          )}
          <div className="flex justify-between">
            <dt>Email verificat</dt>
            <dd>
              {account.emailVerifiedAt ? (
                <span className="text-gray-900">{formatDateTime(account.emailVerifiedAt)}</span>
              ) : (
                <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                  Neverificat
                </span>
              )}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt>Ultima autentificare</dt>
            <dd className="text-gray-900">{formatDateTime(account.lastLoginAt)}</dd>
          </div>
        </dl>
      ) : (
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Această persoană nu are un cont de utilizator asociat.
          </p>
          {canManage && (
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="ui-btn-primary mt-3 inline-flex items-center gap-1.5"
            >
              <UserPlus size={15} />
              Creează cont
            </button>
          )}
        </div>
      )}

      {account && !account.emailVerifiedAt && (
        <UnverifiedEmailNotice person={person} canManage={canManage} />
      )}

      {showEdit && account && (
        <EditAccountModal person={person} onClose={() => setShowEdit(false)} />
      )}
      {showCreate && !account && (
        <CreateAccountModal person={person} onClose={() => setShowCreate(false)} />
      )}
    </div>
  );
}

/**
 * Warning shown when the linked account never confirmed its address. Login
 * refuses such an account outright (EMAIL_NOT_VERIFIED), so the member looks
 * active in Echipă yet cannot sign in — and nothing in the app said why until
 * this notice. Only self-registered accounts land here; every account an admin
 * creates or links is verified on the spot.
 */
function UnverifiedEmailNotice({ person, canManage }: { person: Person; canManage: boolean }) {
  const verify = useVerifyPersonEmail(person.id);
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 p-3">
      <p className="text-xs text-amber-900">
        Contul nu și-a confirmat adresa de email, deci autentificarea îi este refuzată. Dacă mesajul
        de confirmare nu a ajuns niciodată, îl poți valida manual — echivalent cu a confirma tu că
        adresa îi aparține.
      </p>
      {canManage && (
        <button
          type="button"
          onClick={() => {
            if (verify.isPending) return;
            setError(null);
            verify.mutate(undefined, {
              onError: (err) =>
                setError(err instanceof Error ? err.message : 'Verificarea manuală a eșuat'),
            });
          }}
          disabled={verify.isPending}
          className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-900 transition hover:bg-amber-100 disabled:opacity-50"
        >
          <MailCheck size={13} />
          {verify.isPending ? 'Se verifică…' : 'Verifică manual emailul'}
        </button>
      )}
      {error && <p className="mt-2 text-xs text-red-600">{error}</p>}
    </div>
  );
}

/** Edit the linked account: login email, display name, role, active flag. */
function EditAccountModal({ person, onClose }: { person: Person; onClose: () => void }) {
  const account = person.account!;
  const update = useUpdatePersonAccount(person.id);

  const [email, setEmail] = useState(account.email);
  const [name, setName] = useState(account.name);
  const [role, setRole] = useState<UserRole>(account.role);
  const [isActive, setIsActive] = useState(account.isActive);
  const [error, setError] = useState<string | null>(null);

  const isValid = EMAIL_RE.test(email.trim()) && name.trim().length > 0;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || update.isPending) return;
    setError(null);
    update.mutate(
      {
        email: email.trim(),
        name: name.trim(),
        role,
        isActive,
      },
      {
        onSuccess: onClose,
        onError: (err) =>
          setError(err instanceof Error ? err.message : 'Actualizarea contului a eșuat'),
      },
    );
  }

  return (
    <div
      className="ui-modal-backdrop"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ui-modal-panel w-full max-w-[440px] p-0">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h3 className="text-base font-semibold text-gray-900">Editează contul</h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Închide"
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-4 px-5 py-4">
            <label className="block text-sm font-medium text-gray-700">
              Email autentificare
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="ui-input mt-1"
              />
            </label>
            <label className="block text-sm font-medium text-gray-700">
              Nume afișat
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="ui-input mt-1"
              />
            </label>
            <Select
              label="Rol"
              value={role}
              onChange={(v) => setRole(v as UserRole)}
              options={(Object.keys(ROLE_LABELS) as UserRole[]).map((r) => ({
                value: r,
                label: ROLE_LABELS[r],
              }))}
            />
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              Cont activ
            </label>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
          <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
            <button type="button" onClick={onClose} className="ui-btn-secondary">
              Anulează
            </button>
            <button
              type="submit"
              disabled={!isValid || update.isPending}
              className="ui-btn-primary"
            >
              {update.isPending ? 'Se salvează…' : 'Salvează'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Deferred account creation for a person without a login. Mirrors the
 * Account & Access section of the Create Team Member modal: invite vs manual
 * password, optional welcome email and a role-template selection.
 */
function CreateAccountModal({ person, onClose }: { person: Person; onClose: () => void }) {
  const create = useCreatePersonAccount(person.id);
  const { data: templates } = useRoleTemplates();

  const [accountMode, setAccountMode] = useState<'invite' | 'password'>('invite');
  const [password, setPassword] = useState(() => generatePassword());
  const [showPassword, setShowPassword] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const [templateId, setTemplateId] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Post-success state: invite confirmation, one-time password reveal or
  // existing-account link confirmation.
  const [result, setResult] = useState<{
    invited: boolean;
    generatedPassword: string | null;
    linked?: boolean;
  } | null>(null);

  const isValid = accountMode === 'invite' || isStrongPassword(password);

  async function copyToClipboard(value: string) {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable (insecure context); ignore silently.
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid || create.isPending) return;
    setError(null);
    create.mutate(
      {
        accountMode,
        password: accountMode === 'password' ? password : undefined,
        sendWelcomeEmail: accountMode === 'password' ? sendWelcomeEmail : false,
        roleTemplateId: templateId || null,
      },
      {
        onSuccess: (res) =>
          setResult({
            invited: res.invited,
            generatedPassword: res.generatedPassword,
            linked: res.linked,
          }),
        onError: (err) => setError(err instanceof Error ? err.message : 'Crearea contului a eșuat'),
      },
    );
  }

  return (
    <div
      className="ui-modal-backdrop"
      role="dialog"
      aria-modal="true"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !result) onClose();
      }}
    >
      <div className="ui-modal-panel w-full max-w-[480px] p-0">
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <UserPlus size={16} className="text-brand-600" />
            Creează cont pentru {person.fullName}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
            aria-label="Închide"
          >
            <X size={18} />
          </button>
        </div>

        {result ? (
          <div className="px-5 py-4">
            {result.linked ? (
              <p className="text-sm text-gray-600">
                Există deja un cont cu adresa{' '}
                <span className="font-medium text-gray-900">{person.email}</span>. Contul existent a
                fost asociat acestei persoane; parola și permisiunile lui nu au fost modificate.
              </p>
            ) : result.invited ? (
              <p className="text-sm text-gray-600">
                Invitație trimisă la{' '}
                <span className="font-medium text-gray-900">{person.email}</span>. Persoana își va
                seta parola din linkul primit (valabil 7 zile).
              </p>
            ) : result.generatedPassword ? (
              <>
                <p className="text-sm text-gray-500">
                  Email-ul de bun venit nu a putut fi trimis (SMTP nu este configurat) sau a fost
                  omis. Distribuie aceste date de autentificare în siguranță — această parolă nu va
                  mai fi afișată.
                </p>
                <div className="mt-4 space-y-2 rounded-lg border border-border bg-gray-50 p-3 text-sm">
                  <div className="flex justify-between gap-2">
                    <span className="text-gray-500">Email</span>
                    <span className="font-medium text-gray-900">{person.email}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-gray-500">Parolă</span>
                    <span className="flex items-center gap-2">
                      <code className="rounded bg-white px-2 py-0.5 font-mono text-gray-900">
                        {result.generatedPassword}
                      </code>
                      <button
                        type="button"
                        onClick={() => void copyToClipboard(result.generatedPassword!)}
                        className="rounded p-1 text-gray-400 hover:text-gray-600"
                        aria-label="Copiază parola"
                      >
                        {copied ? (
                          <Check size={15} className="text-emerald-500" />
                        ) : (
                          <Copy size={15} />
                        )}
                      </button>
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-600">
                Contul a fost creat, iar datele de autentificare au fost trimise pe email la{' '}
                <span className="font-medium text-gray-900">{person.email}</span>.
              </p>
            )}
            <div className="mt-5 flex justify-end">
              <button type="button" onClick={onClose} className="ui-btn-primary">
                Gata
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="flex flex-col gap-4 px-5 py-4">
              <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-500">
                Contul va folosi adresa{' '}
                <span className="font-medium text-gray-700">{person.email}</span> pentru
                autentificare.
              </p>

              {/* Account mode: invite email vs manual password */}
              <div className="flex flex-col gap-2 text-sm text-gray-700">
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="person-account-mode"
                    checked={accountMode === 'invite'}
                    onChange={() => setAccountMode('invite')}
                    className="h-4 w-4 border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  Trimite invitație pe email
                  <span className="font-normal text-gray-400">
                    — persoana își setează singură parola
                  </span>
                </label>
                <label className="flex cursor-pointer items-center gap-2">
                  <input
                    type="radio"
                    name="person-account-mode"
                    checked={accountMode === 'password'}
                    onChange={() => setAccountMode('password')}
                    className="h-4 w-4 border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  Setează parola manual
                </label>
              </div>

              {accountMode === 'invite' && (
                <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-500">
                  Persoana va primi un email cu un link (valabil 7 zile) pentru a-și activa contul
                  și a-și seta parola. Dacă SMTP nu este configurat, se revine automat la modul cu
                  parolă.
                </p>
              )}

              {accountMode === 'password' && (
                <>
                  <div className="block text-sm font-medium text-gray-700">
                    Parolă
                    <div className="mt-1 flex gap-2">
                      <div className="relative flex-1">
                        <input
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="ui-input pr-20 font-mono"
                        />
                        <div className="absolute right-1 top-1/2 flex -translate-y-1/2 items-center">
                          <button
                            type="button"
                            onClick={() => setShowPassword((s) => !s)}
                            className="rounded p-1.5 text-gray-400 hover:text-gray-600"
                            aria-label={showPassword ? 'Ascunde parola' : 'Arată parola'}
                          >
                            {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                          </button>
                          <button
                            type="button"
                            onClick={() => void copyToClipboard(password)}
                            className="rounded p-1.5 text-gray-400 hover:text-gray-600"
                            aria-label="Copiază parola"
                          >
                            {copied ? (
                              <Check size={15} className="text-emerald-500" />
                            ) : (
                              <Copy size={15} />
                            )}
                          </button>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setPassword(generatePassword())}
                        className="ui-btn-secondary inline-flex items-center gap-1.5 whitespace-nowrap"
                      >
                        <RefreshCw size={14} />
                        Generează
                      </button>
                    </div>
                    {!isStrongPassword(password) && (
                      <p className="mt-1 text-sm text-red-600">
                        Minim 8 caractere, cu 1 literă mare și 1 cifră
                      </p>
                    )}
                  </div>
                  <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={sendWelcomeEmail}
                      onChange={(e) => setSendWelcomeEmail(e.target.checked)}
                      className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    Trimite email de bun venit cu datele de autentificare
                  </label>
                </>
              )}

              <Select
                label="Șablon de rol (permisiuni)"
                clearable
                value={templateId}
                onChange={setTemplateId}
                placeholder="Fără șablon — acces implicit minim"
                options={(templates ?? []).map((t) => ({ value: t.id, label: t.name }))}
              />

              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>
            <div className="flex justify-end gap-2 border-t border-border px-5 py-4">
              <button type="button" onClick={onClose} className="ui-btn-secondary">
                Anulează
              </button>
              <button
                type="submit"
                disabled={!isValid || create.isPending}
                className="ui-btn-primary"
              >
                {create.isPending ? 'Se creează…' : 'Creează cont'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
